import Link from "next/link"
import { GitCompare, ArrowRight } from "lucide-react"

// Sensible head-to-head comparison pairings. Each grass surfaces the
// comparison most-asked by readers landing on that guide.
const COMPARISON_PARTNER: Record<string, { partnerSlug: string; partnerDisplay: string }> = {
  bermudagrass: { partnerSlug: "zoysiagrass", partnerDisplay: "Zoysiagrass" },
  zoysiagrass: { partnerSlug: "bermudagrass", partnerDisplay: "Bermudagrass" },
  "st-augustinegrass": { partnerSlug: "zoysiagrass", partnerDisplay: "Zoysiagrass" },
  centipede: { partnerSlug: "st-augustinegrass", partnerDisplay: "St. Augustinegrass" },
  bahiagrass: { partnerSlug: "bermudagrass", partnerDisplay: "Bermudagrass" },
  buffalograss: { partnerSlug: "bermudagrass", partnerDisplay: "Bermudagrass" },
  "kentucky-bluegrass": { partnerSlug: "tall-fescue", partnerDisplay: "Tall Fescue" },
  "tall-fescue": { partnerSlug: "kentucky-bluegrass", partnerDisplay: "Kentucky Bluegrass" },
  "fine-fescue": { partnerSlug: "kentucky-bluegrass", partnerDisplay: "Kentucky Bluegrass" },
  "perennial-ryegrass": { partnerSlug: "kentucky-bluegrass", partnerDisplay: "Kentucky Bluegrass" },
}

interface PillarCompareCardProps {
  grassSlug: string
  grassDisplay: string
}

export function PillarCompareCard({ grassSlug, grassDisplay }: PillarCompareCardProps) {
  const pair = COMPARISON_PARTNER[grassSlug]
  if (!pair) return null
  const href = `/tools/grass-comparison?grasses=${grassSlug},${pair.partnerSlug}`
  return (
    <Link
      href={href}
      className="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-emerald-200/80 bg-linear-to-r from-emerald-50/70 via-white to-emerald-50/40 px-4 py-3.5 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md md:px-5"
    >
      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white text-emerald-700 ring-1 ring-emerald-200/80 ring-inset transition-colors group-hover:bg-emerald-50">
        <GitCompare className="h-4.5 w-4.5" strokeWidth={2.2} />
      </div>
      <div className="flex-1 text-sm leading-snug text-gray-700 md:text-[15px]">
        <span className="font-semibold text-gray-900">{grassDisplay}</span>
        <span className="mx-1.5 text-xs font-bold uppercase tracking-wider text-emerald-700/80">
          vs
        </span>
        <span className="font-semibold text-gray-900">{pair.partnerDisplay}</span>
        <span className="ml-1.5 text-gray-600">
          — drought, traffic, shade, and maintenance, side by side.
        </span>
      </div>
      <span
        aria-hidden="true"
        className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm transition-transform group-hover:translate-x-0.5 group-hover:bg-emerald-700"
      >
        <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
      </span>
    </Link>
  )
}
